import { useCallback, useEffect, useRef, useState } from 'react';
import { Room } from 'livekit-client';

import { reportError } from '../../../lib/sentry';
import { DeviceLists, LocalDevices } from '../types';

const groupByKind = (devices: MediaDeviceInfo[]): DeviceLists => ({
  videoinput: devices.filter((device) => device.kind === 'videoinput'),
  audioinput: devices.filter((device) => device.kind === 'audioinput'),
  audiooutput: devices.filter((device) => device.kind === 'audiooutput'),
});

// Call refresh when the list is about to be shown: a list read before the
// browser allowed the camera holds only unnamed placeholders, and Chrome sends
// no devicechange when access is allowed. The call has already asked for the
// camera and microphone by then, so reading never prompts again.
export const useLocalDevices = (): {
  state: LocalDevices;
  refresh: () => void;
} => {
  const [state, setState] = useState<LocalDevices>({ kind: 'idle' });
  // Opening the menu and plugging in a device can start reads that overlap.
  // The last read wins, and an older one that finishes later is dropped.
  const latestReadId = useRef(0);

  const refresh = useCallback(() => {
    latestReadId.current += 1;
    const readId = latestReadId.current;
    const isLatestRead = () => readId === latestReadId.current;
    setState((current) =>
      current.kind === 'ready' ? current : { kind: 'loading' }
    );
    Room.getLocalDevices(undefined, false)
      .then((devices) => {
        if (isLatestRead()) {
          setState({ kind: 'ready', devices: groupByKind(devices) });
        }
      })
      .catch((err: unknown) => {
        reportError(err, { tags: { feature: 'video' } });
        if (isLatestRead()) setState({ kind: 'error' });
      });
  }, []);

  useEffect(() => {
    const { mediaDevices } = navigator;
    mediaDevices?.addEventListener('devicechange', refresh);
    return () => {
      mediaDevices?.removeEventListener('devicechange', refresh);
      // A read still running after unmount is no longer the latest.
      latestReadId.current += 1;
    };
  }, [refresh]);

  return { state, refresh };
};
